/**
 * PANDA-1523 — post-signup personalization wizard.
 *
 * A short, one-time, 3-step wizard shown after a user's first authenticated
 * landing (signup + email verification, magic link, or OAuth) and before
 * their submitted prompt hands off into generation. Captures theme
 * preference, professional role, and company size — separate from the
 * per-project ownership questions in lib/ownership/ownership-questions.ts,
 * which scope what gets BUILT rather than who's building it.
 */

export type ThemeChoice = "light" | "dark";

export interface RoleOption {
  value: string;
  label: string;
}

export interface CompanySizeOption {
  value: string;
  label: string;
}

export const ROLE_OPTIONS: RoleOption[] = [
  { value: "founder", label: "Founder" },
  { value: "designer", label: "Designer" },
  { value: "engineer", label: "Engineer" },
  { value: "consultant", label: "Consultant" },
  { value: "marketing_sales", label: "Marketing / Sales" },
  { value: "operations_other", label: "Operations / Other" },
];

export const COMPANY_SIZE_OPTIONS: CompanySizeOption[] = [
  { value: "1", label: "Just me" },
  { value: "2-10", label: "2 - 10" },
  { value: "11-200", label: "11 - 200" },
  { value: "201+", label: "201+" },
];

const ROLE_LABELS = new Map(ROLE_OPTIONS.map((o) => [o.value, o.label]));
const COMPANY_SIZE_LABELS = new Map(
  COMPANY_SIZE_OPTIONS.map((o) => [o.value, o.label]),
);

// ── Analytics vocabulary ───────────────────────────────────────────────────
//
// The wizard's PostHog events (lib/analytics.ts) report a NORMALIZED value per
// answer rather than the display label or the raw storage value, so a funnel
// breakdown reads `just_me` / `dark_mode` instead of "Just me" / "dark". The
// storage values above are untouched — they are what the DB and the prompt
// context block already use, and renaming them would invalidate existing rows.

/** Stable analytics identity of each wizard question. */
export type OnboardingQuestionKey = "style_preference" | "role" | "company_size";

/**
 * The wizard's steps in render order. MUST stay in sync with STEP_TITLES in
 * components/onboarding/personalization-wizard-inline.tsx — the wizard indexes
 * this array by its current step to label events.
 */
export const ONBOARDING_STEP_KEYS: readonly OnboardingQuestionKey[] = [
  "style_preference",
  "role",
  "company_size",
];

export const ONBOARDING_TOTAL_STEPS = ONBOARDING_STEP_KEYS.length;

const THEME_ANALYTICS_VALUES: Record<string, string> = {
  light: "light_mode",
  dark: "dark_mode",
};

const COMPANY_SIZE_ANALYTICS_VALUES: Record<string, string> = {
  "1": "just_me",
  "2-10": "2_10",
  "11-200": "11_200",
  "201+": "201_plus",
};

/**
 * Storage value → analytics value. Role values are already normalized
 * (`founder`, `marketing_sales`, …) so they pass through unchanged; anything
 * unrecognised also passes through rather than being dropped, so a future
 * option that forgets to register here still produces a usable event.
 */
export function onboardingAnalyticsValue(
  key: OnboardingQuestionKey,
  value: string | null | undefined,
): string | null {
  if (!value) return null;
  if (key === "style_preference") return THEME_ANALYTICS_VALUES[value] ?? value;
  if (key === "company_size") return COMPANY_SIZE_ANALYTICS_VALUES[value] ?? value;
  return value;
}

/** Display label for an answer, for readability in event payloads. */
export function onboardingOptionLabel(
  key: OnboardingQuestionKey,
  value: string | null | undefined,
): string | null {
  if (!value) return null;
  if (key === "style_preference") return value === "dark" ? "Dark Mode" : "Light Mode";
  if (key === "role") return ROLE_LABELS.get(value) ?? value;
  return COMPANY_SIZE_LABELS.get(value) ?? value;
}

export interface PersonalizationAnswers {
  theme: ThemeChoice | null;
  role: string | null;
  companySize: string | null;
}

export function isValidTheme(value: unknown): value is ThemeChoice {
  return value === "light" || value === "dark";
}

export function isValidRole(value: unknown): value is string {
  return typeof value === "string" && ROLE_LABELS.has(value);
}

export function isValidCompanySize(value: unknown): value is string {
  return typeof value === "string" && COMPANY_SIZE_LABELS.has(value);
}

/** The shape GET /api/user/onboarding reports back. */
export interface OnboardingStatus {
  /** They answered every question, or pressed "Skip for now". */
  completed?: boolean | null;
  /** We put the wizard on screen. Stamped before the user acts. */
  shown?: boolean | null;
}

/**
 * The one-time gate, in one place because both surfaces have to agree — a
 * surface that got this wrong is exactly how the wizard came back (QA:
 * "Onboarding questions reappear after users dismiss, skip, or refresh").
 *
 * `shown` is stamped the instant the wizard renders, `completed` when it is
 * answered or skipped. EITHER means never ask again: a user who dismissed by
 * refreshing never completed anything, and they must not be asked twice.
 *
 * Fails CLOSED. A null payload — no row, a failed read, an unparsed response —
 * means "don't ask". Guessing "not shown yet" from missing information is the
 * bug: it re-asks on every load that happens to fail.
 *
 * "Closed" has to mean BOTH FIELDS PRESENT AND FALSE, not merely falsy. The
 * previous `!status.completed && !status.shown` only failed closed on a null
 * payload: for `{}`, for `{ error: "…" }`, or for any 200 whose body was not the
 * shape we expect, both reads were `undefined` and the expression came out
 * `true` — "show the wizard". That is the same wrong answer this function was
 * written to prevent, reached through a response that arrived rather than one
 * that failed. Both fields are declared optional on OnboardingStatus, so the
 * type system permits it and only this check rules it out.
 */
export function shouldShowOnboardingWizard(
  status: OnboardingStatus | null | undefined,
): boolean {
  if (!status) return false;
  return status.completed === false && status.shown === false;
}

// The first-build context block used to live here as buildUserContextPromptBlock.
// It named the answers ("role: Founder, company size: 2 - 10") without saying
// what to do with them, so it changed nothing observable. It now lives in
// ./personalization-profile as buildPersonalizationPromptBlock, which renders
// real guidance from a single shared mapping and withholds the visual default
// when the user has already stated one.
